import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tab,
  Tabs,
  Snackbar,
  Alert,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  Business as BusinessIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  VpnKey as VpnKeyIcon,
  AttachMoney as MoneyIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Language as LanguageIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Engineering as EngineeringIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminIcon,
  Phone as PhoneIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material/Select';

import { useAuth } from '../contexts/AuthContext';
import {
  addDeviceType,
  getAllDeviceTypes,
  updateDeviceType,
  deleteDeviceType,
  setDefaultDeviceType,
  addDeviceSize,
  getAllDeviceSizes,
  updateDeviceSize,
  deleteDeviceSize,
  addDeviceAccessory,
  getAllDeviceAccessories,
  updateDeviceAccessory,
  deleteDeviceAccessory,
  addDeviceBrand,
  getAllDeviceBrands,
  updateDeviceBrand,
  deleteDeviceBrand
} from '../services/settingsService';
import {
  MaintenanceStatusCategoryService,
  MaintenanceStatusSubCategoryService
} from '../services/maintenanceStatusService';
import {
  MaintenanceStatusCategory,
  MaintenanceStatusSubCategory
} from '../types/maintenanceStatus';
import { addDefaultMaintenanceStatusData } from '../utils/defaultMaintenanceStatus';
import { getSystemSettings, saveSystemSettings, SystemSettings, DEFAULT_SETTINGS } from '../services/systemSettingsService';
import {
  getAllUsers,
  createUser,
  updateUser,
  deleteUserById,
  setUserActive,
  updateUserPermissions
} from '../services/userService';
import { getAllBranches, getBranchById } from '../services/branchService';
import { getAllWarehouses } from '../services/inventoryService';
import { DeviceTypeModel, DeviceSizeModel, DeviceAccessoryModel, DeviceBrandModel, User, UserRole, UserPermissions, Branch, Warehouse } from '../utils/types';
import { currencies } from '../utils/constants';
import { getDefaultPermissions } from '../services/permissionService';
import { updateFavicon, restoreDefaultFavicon } from '../utils/faviconUtils';
import UrgentMessageManager from '../components/UrgentMessageManager';
import PrintSettings from '../components/PrintSettings';

// تعريف نوع المستخدم المعروض في الواجهة
interface UserData extends User {
  photoURL?: string;
  password?: string;
  confirmPassword?: string;
  selectedBranch?: string;
  selectedWarehouses?: string[];
}

// نوع الإعدادات العامة مستورد من systemSettingsService

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

const SettingsPage: React.FC = () => {
  const { currentUser } = useAuth();

  // حالة التبويبات
  const [currentTab, setCurrentTab] = useState<string>('general');

  // حالة الإعدادات
  const [settings, setSettings] = useState<SystemSettings>(DEFAULT_SETTINGS);

  // حالة قائمة المستخدمين
  const [users, setUsers] = useState<UserData[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // حالة الفروع والمخازن
  const [branches, setBranches] = useState<Branch[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);

  // حالة نافذة حوار إضافة/تعديل مستخدم
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [currentUser_, setCurrentUser_] = useState<UserData | null>(null);

  // حالات حالات الصيانة
  const [maintenanceStatusCategories, setMaintenanceStatusCategories] = useState<MaintenanceStatusCategory[]>([]);
  const [maintenanceStatusSubCategories, setMaintenanceStatusSubCategories] = useState<MaintenanceStatusSubCategory[]>([]);
  const [loadingMaintenanceStatus, setLoadingMaintenanceStatus] = useState(false);
  const [maintenanceStatusDialogOpen, setMaintenanceStatusDialogOpen] = useState(false);
  const [currentMaintenanceStatus, setCurrentMaintenanceStatus] = useState<MaintenanceStatusCategory | MaintenanceStatusSubCategory | null>(null);
  const [maintenanceStatusDialogType, setMaintenanceStatusDialogType] = useState<'category' | 'subcategory'>('category');
  const [newUser, setNewUser] = useState<Partial<UserData>>({
    name: '',
    email: '',
    role: UserRole.TECHNICIAN,
    isActive: true,
    selectedBranch: '',
    selectedWarehouses: [],
    permissions: {
      // صلاحيات الوصول للصفحات
      canAccessDashboard: true,
      canAccessInventory: false,
      canAccessPOS: false,
      canAccessMaintenance: true,
      canAccessCustomers: false,
      canAccessSettings: false,
      canAccessReports: false,
      canModifyInventory: false,
      canAccessDebts: false,
      canViewExpenses: false,
      canViewBasicExpenses: false,
      canViewDailyExpenses: false,
      canAddExpenses: false,
      canEditExpenses: false,
      canDeleteExpenses: false,
      canManageBackup: false,

      // صلاحيات المخزون
      canViewInventory: false,
      canAddInventory: false,
      canEditInventory: false,
      canDeleteInventory: false,
      canTransferInventory: false,

      // صلاحيات المبيعات
      canViewSales: false,
      canCreateSales: false,
      canEditSales: false,
      canDeleteSales: false,
      canApplyDiscount: false,

      // صلاحيات الصيانة
      canViewMaintenance: true,
      canCreateMaintenance: true,
      canEditMaintenance: true,
      canDeleteMaintenance: true,
      canAssignTechnician: true,
      canChangeMaintStatus: true,

      // صلاحيات العملاء
      canViewCustomers: false,
      canAddCustomers: false,
      canEditCustomers: false,
      canDeleteCustomers: false,

      // صلاحيات الفروع والمخازن
      canManageBranches: false,
      canManageWarehouses: false,

      // صلاحيات المستخدمين
      canManageUsers: false,
      canViewUsers: false,

      // صلاحيات الإعدادات
      canManageSettings: false
    }
  });

  // حالة نافذة حوار تأكيد حذف المستخدم
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);

  // حالة نافذة حوار إدارة صلاحيات المستخدم
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [userForPermissions, setUserForPermissions] = useState<UserData | null>(null);

  // حالة نافذة حوار تغيير كلمة المرور
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // حالة رسائل التنبيه
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // حالة التبويب النشط
  const [activeTab, setActiveTab] = useState(0);

  // حالة لكل نوع من البيانات
  const [deviceTypes, setDeviceTypes] = useState<DeviceTypeModel[]>([]);
  const [deviceSizes, setDeviceSizes] = useState<DeviceSizeModel[]>([]);
  const [deviceAccessories, setDeviceAccessories] = useState<DeviceAccessoryModel[]>([]);
  const [deviceBrands, setDeviceBrands] = useState<DeviceBrandModel[]>([]);

  // حالة التحميل
  const [loading, setLoading] = useState({
    types: false,
    sizes: false,
    accessories: false,
    brands: false
  });

  // حالة مربع الحوار
  const [dialog, setDialog] = useState({
    open: false,
    title: '',
    message: '',
    itemId: '',
    itemType: '',
    confirmAction: () => {}
  });

  // حالة الإضافة/التعديل
  const [itemForm, setItemForm] = useState({
    id: '',
    name: '',
    isActive: true,
    itemType: ''
  });

  // استرجاع إعدادات الشركة عند تحميل الصفحة
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const systemSettings = await getSystemSettings();
        setSettings(systemSettings);

        // تحديث أيقونة الموقع إذا كانت موجودة في الإعدادات
        if (systemSettings.companyFavicon) {
          updateFavicon(systemSettings.companyFavicon);
        }
      } catch (error) {
        console.error('Error loading system settings:', error);
      }
    };

    loadSettings();

    // تحميل بيانات أنواع الأجهزة وأحجامها وملحقاتها وماركاتها
    loadDeviceTypes();
    loadDeviceSizes();
    loadDeviceAccessories();
    loadDeviceBrands();

    // تحميل بيانات المستخدمين والفروع والمخازن
    loadUsers();
    loadBranches();
    loadWarehouses();

    // استعادة الأيقونة الافتراضية عند مغادرة الصفحة
    return () => {
      restoreDefaultFavicon();
    };
  }, []);

  // تحميل المستخدمين
  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const usersList = await getAllUsers(true); // تضمين المستخدمين غير النشطين
      setUsers(usersList as UserData[]);
    } catch (error) {
      console.error('Error loading users:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل بيانات المستخدمين',
        severity: 'error'
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  // تحميل الفروع
  const loadBranches = async () => {
    try {
      setLoadingBranches(true);
      const branchesList = await getAllBranches(true); // تضمين الفروع غير النشطة
      setBranches(branchesList);
    } catch (error) {
      console.error('Error loading branches:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل بيانات الفروع',
        severity: 'error'
      });
    } finally {
      setLoadingBranches(false);
    }
  };

  // تحميل المخازن
  const loadWarehouses = async () => {
    try {
      setLoadingWarehouses(true);
      const warehousesList = await getAllWarehouses();
      setWarehouses(warehousesList);
    } catch (error) {
      console.error('Error loading warehouses:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل بيانات المخازن',
        severity: 'error'
      });
    } finally {
      setLoadingWarehouses(false);
    }
  };

  // تغيير التبويب الحالي
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  };

  // الانتقال إلى صفحة الإعدادات المتقدمة
  const handleGoToAdvancedSettings = () => {
    window.location.href = '/admin/advanced-settings';
  };

  // تحديث الإعدادات العامة
  const handleSettingsChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value, checked, type } = e.target as HTMLInputElement;

    let processedValue: any = type === 'checkbox' ? checked : value;

    // تحويل القيم الرقمية إلى numbers
    if (name === 'phoneDigitsCount' || name === 'vatPercentage') {
      processedValue = parseInt(value as string, 10) || 0;
    }

    setSettings(prev => ({
      ...prev,
      [name!]: processedValue
    }));
  };

  // تحديث الإعدادات لمكونات Select
  const handleSelectChange = (e: SelectChangeEvent<string>, child: React.ReactNode) => {
    const { name, value } = e.target;

    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // تحميل لوجو الشركة
  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setSettings({
          ...settings,
          companyLogo: e.target?.result as string
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // تحميل أيقونة الموقع (Favicon)
  const handleFaviconUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const faviconDataUrl = e.target?.result as string;
        setSettings({
          ...settings,
          companyFavicon: faviconDataUrl
        });

        // تحديث أيقونة الموقع في الصفحة مباشرة
        updateFavicon(faviconDataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  // حفظ الإعدادات
  const handleSaveSettings = async () => {
    try {
      // حفظ الإعدادات باستخدام الخدمة الجديدة
      await saveSystemSettings(settings);

      console.log('Settings saved successfully:', settings);

      // تحديث أيقونة الموقع بعد حفظ الإعدادات
      if (settings.companyFavicon) {
        updateFavicon(settings.companyFavicon);
      } else {
        restoreDefaultFavicon();
      }

      setSnackbar({
        open: true,
        message: 'تم حفظ الإعدادات بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving settings:', error);

      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حفظ الإعدادات',
        severity: 'error'
      });
    }
  };

  // فتح نافذة حوار إضافة مستخدم جديد
  const handleAddUser = () => {
    const defaultRole = UserRole.TECHNICIAN;
    setCurrentUser_(null);
    setNewUser({
      name: '',
      email: '',
      role: defaultRole,
      isActive: true,
      selectedBranch: '',
      selectedWarehouses: [],
      permissions: getDefaultPermissions(defaultRole)
    });
    setUserDialogOpen(true);
  };

  // فتح نافذة حوار تعديل مستخدم
  const handleEditUser = (user: UserData) => {
    setCurrentUser_(user);
    setNewUser({
      ...user,
      selectedBranch: user.branchId || '',
      selectedWarehouses: user.allowedWarehouses || []
    });
    setUserDialogOpen(true);
  };

  // حفظ بيانات المستخدم
  const handleSaveUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.role) {
      setSnackbar({
        open: true,
        message: 'يرجى ملء جميع الحقول المطلوبة',
        severity: 'error'
      });
      return;
    }

    try {
      if (currentUser_) {
        // تحديث مستخدم موجود
        const updatedUser: Partial<User> = {
          name: newUser.name,
          role: newUser.role,
          isActive: newUser.isActive,
          branchId: newUser.selectedBranch || undefined,
          allowedWarehouses: newUser.selectedWarehouses || [],
          // الحفاظ على الصلاحيات الحالية إذا لم يتم تغييرها
          permissions: currentUser_.permissions || getDefaultPermissions(newUser.role)
        };

        // الحصول على اسم الفرع إذا تم تحديده
        if (newUser.selectedBranch) {
          const branch = await getBranchById(newUser.selectedBranch);
          if (branch) {
            updatedUser.branchName = branch.name;
          }
        }

        console.log('Updating user with data:', updatedUser);
        await updateUser(currentUser_.id, updatedUser);

        // تحديث قائمة المستخدمين المحلية
        setUsers(prev => prev.map(user =>
          user.id === currentUser_.id ?
          { ...user, ...updatedUser } :
          user
        ));

        setSnackbar({
          open: true,
          message: 'تم تحديث بيانات المستخدم بنجاح',
          severity: 'success'
        });
      } else {
        // إضافة مستخدم جديد
        if (!newUser.password || !newUser.confirmPassword) {
          setSnackbar({
            open: true,
            message: 'يرجى إدخال كلمة المرور وتأكيدها',
            severity: 'error'
          });
          return;
        }

        if (newUser.password !== newUser.confirmPassword) {
          setSnackbar({
            open: true,
            message: 'كلمة المرور وتأكيدها غير متطابقين',
            severity: 'error'
          });
          return;
        }

        // تحضير الصلاحيات الافتراضية للمستخدم الجديد
        const defaultPerms = getDefaultPermissions(newUser.role!);
        console.log('Creating user with default permissions:', defaultPerms);

        // إنشاء المستخدم الجديد
        const userId = await createUser(
          newUser.email!,
          newUser.password,
          newUser.name!,
          newUser.role!,
          newUser.selectedBranch || undefined,
          newUser.selectedWarehouses || [],
          defaultPerms
        );

        // تفعيل المستخدم إذا تم تحديده
        if (newUser.isActive) {
          await setUserActive(userId, true);
        }

        // إعادة تحميل قائمة المستخدمين
        loadUsers();

        setSnackbar({
          open: true,
          message: 'تم إضافة المستخدم بنجاح',
          severity: 'success'
        });
      }

      setUserDialogOpen(false);
    } catch (error) {
      console.error('Error saving user:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حفظ بيانات المستخدم: ' + (error instanceof Error ? error.message : String(error)),
        severity: 'error'
      });
    }
  };

  // فتح نافذة حوار تأكيد حذف المستخدم
  const handleDeleteConfirmation = (userId: string) => {
    setUserToDelete(userId);
    setDeleteUserDialogOpen(true);
  };

  // حذف مستخدم
  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setDeleteUserLoading(true);
      await deleteUserById(userToDelete);

      // تحديث قائمة المستخدمين المحلية
      setUsers(prev => prev.filter(user => user.id !== userToDelete));

      setSnackbar({
        open: true,
        message: 'تم حذف المستخدم بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حذف المستخدم',
        severity: 'error'
      });
    } finally {
      setDeleteUserLoading(false);
      setDeleteUserDialogOpen(false);
      setUserToDelete(null);
    }
  };

  // فتح نافذة حوار إدارة صلاحيات المستخدم
  const handleManagePermissions = (user: UserData) => {
    console.log('Managing permissions for user:', user);
    setUserForPermissions(user);
    setUserPermissions(user.permissions || getDefaultPermissions(user.role));
    setPermissionsDialogOpen(true);
  };

  // حفظ صلاحيات المستخدم
  const handleSavePermissions = async () => {
    if (!userForPermissions || !userPermissions) {
      console.error('Missing user or permissions data');
      return;
    }

    try {
      console.log('Saving permissions for user:', userForPermissions.id, userPermissions);

      // الطريقة 1: استخدام دالة updateUserPermissions المخصصة
      await updateUserPermissions(userForPermissions.id, userPermissions);

      // الطريقة 2: استخدام دالة updateUser العامة لتحديث المستخدم بالكامل
      // هذا يضمن تحديث الصلاحيات بشكل صحيح
      await updateUser(userForPermissions.id, {
        permissions: userPermissions
      });

      // تحديث قائمة المستخدمين المحلية
      setUsers(prev => prev.map(user =>
        user.id === userForPermissions.id ?
        { ...user, permissions: userPermissions } :
        user
      ));

      setSnackbar({
        open: true,
        message: 'تم تحديث صلاحيات المستخدم بنجاح',
        severity: 'success'
      });

      // إعادة تحميل قائمة المستخدمين للتأكد من تحديث البيانات
      loadUsers();

      setPermissionsDialogOpen(false);
      setUserForPermissions(null);
    } catch (error) {
      console.error('Error updating user permissions:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحديث صلاحيات المستخدم: ' + (error instanceof Error ? error.message : String(error)),
        severity: 'error'
      });
    }
  };

  // تفعيل أو تعطيل مستخدم
  const handleToggleUserActive = async (userId: string, isActive: boolean) => {
    try {
      await setUserActive(userId, isActive);

      // تحديث قائمة المستخدمين المحلية
      setUsers(prev => prev.map(user =>
        user.id === userId ?
        { ...user, isActive } :
        user
      ));

      setSnackbar({
        open: true,
        message: isActive ? 'تم تفعيل المستخدم بنجاح' : 'تم تعطيل المستخدم بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error toggling user active status:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تغيير حالة المستخدم',
        severity: 'error'
      });
    }
  };

  // فتح نافذة حوار تغيير كلمة المرور
  const handleChangePasswordClick = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordDialogOpen(true);
  };

  // حفظ كلمة المرور الجديدة
  const handleSavePassword = () => {
    // التحقق من تطابق كلمات المرور
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSnackbar({
        open: true,
        message: 'كلمة المرور الجديدة وتأكيدها غير متطابقين',
        severity: 'error'
      });
      return;
    }

    // هنا يتم تغيير كلمة المرور (سيتم تنفيذه عند ربط الواجهة بالخلفية)
    console.log('Changing password');

    setSnackbar({
      open: true,
      message: 'تم تغيير كلمة المرور بنجاح',
      severity: 'success'
    });

    setPasswordDialogOpen(false);
  };

  // إغلاق رسالة التنبيه
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // ترجمة دور المستخدم إلى العربية
  const translateRole = (role: string): string => {
    switch (role) {
      case UserRole.ADMIN:
        return 'مدير النظام';
      case UserRole.BRANCH_MANAGER:
        return 'مدير فرع';
      case UserRole.WAREHOUSE_MANAGER:
        return 'مدير مخزن';
      case UserRole.TECHNICIAN:
        return 'فني صيانة';
      case UserRole.CASHIER:
        return 'أمين صندوق';
      case UserRole.INVENTORY_CLERK:
        return 'موظف مخزن';
      case UserRole.SALES:
        return 'مندوب مبيعات';
      default:
        return role;
    }
  };

  // تحميل البيانات عند تغيير التبويب النشط
  useEffect(() => {
    switch (activeTab) {
      case 0:
        loadDeviceTypes();
        break;
      case 1:
        loadDeviceSizes();
        break;
      case 2:
        loadDeviceAccessories();
        break;
      case 3:
        loadDeviceBrands();
        break;
    }
  }, [activeTab]);

  // تحميل حالات الصيانة عند تغيير التبويب
  useEffect(() => {
    if (currentTab === 'maintenanceStatus') {
      loadMaintenanceStatusData();
    }
  }, [currentTab]);

  // تحميل أنواع الأجهزة
  const loadDeviceTypes = async () => {
    setLoading({ ...loading, types: true });
    try {
      const types = await getAllDeviceTypes();
      setDeviceTypes(types);
    } catch (error) {
      console.error('Error loading device types:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل أنواع الأجهزة',
        severity: 'error'
      });
    } finally {
      setLoading({ ...loading, types: false });
    }
  };

  // تحميل أحجام الأجهزة
  const loadDeviceSizes = async () => {
    setLoading({ ...loading, sizes: true });
    try {
      const sizes = await getAllDeviceSizes();
      setDeviceSizes(sizes);
    } catch (error) {
      console.error('Error loading device sizes:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل أحجام الأجهزة',
        severity: 'error'
      });
    } finally {
      setLoading({ ...loading, sizes: false });
    }
  };

  // تحميل ملحقات الأجهزة
  const loadDeviceAccessories = async () => {
    setLoading({ ...loading, accessories: true });
    try {
      const accessories = await getAllDeviceAccessories();
      setDeviceAccessories(accessories);
    } catch (error) {
      console.error('Error loading device accessories:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل ملحقات الأجهزة',
        severity: 'error'
      });
    } finally {
      setLoading({ ...loading, accessories: false });
    }
  };

  // تحميل ماركات الأجهزة
  const loadDeviceBrands = async () => {
    setLoading({ ...loading, brands: true });
    try {
      const brands = await getAllDeviceBrands();
      setDeviceBrands(brands);
    } catch (error) {
      console.error('Error loading device brands:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل ماركات الأجهزة',
        severity: 'error'
      });
    } finally {
      setLoading({ ...loading, brands: false });
    }
  };

  // دوال حالات الصيانة
  const loadMaintenanceStatusData = async () => {
    setLoadingMaintenanceStatus(true);
    try {
      const [categories, subCategories] = await Promise.all([
        MaintenanceStatusCategoryService.getCategories(),
        MaintenanceStatusSubCategoryService.getSubCategories()
      ]);
      setMaintenanceStatusCategories(categories);
      setMaintenanceStatusSubCategories(subCategories);
    } catch (error) {
      console.error('Error loading maintenance status data:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل حالات الصيانة',
        severity: 'error'
      });
    } finally {
      setLoadingMaintenanceStatus(false);
    }
  };

  const handleAddMaintenanceStatus = (type: 'category' | 'subcategory') => {
    setMaintenanceStatusDialogType(type);
    if (type === 'category') {
      setCurrentMaintenanceStatus({
        id: '',
        name: '',
        description: '',
        icon: '',
        color: '',
        order: 0,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: '',
        branchId: undefined
      } as MaintenanceStatusCategory);
    } else {
      setCurrentMaintenanceStatus({
        id: '',
        categoryId: '',
        name: '',
        description: '',
        icon: '',
        color: '',
        order: 0,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: '',
        branchId: undefined
      } as MaintenanceStatusSubCategory);
    }
    setMaintenanceStatusDialogOpen(true);
  };

  const handleEditMaintenanceStatus = (item: MaintenanceStatusCategory | MaintenanceStatusSubCategory, type: 'category' | 'subcategory') => {
    setMaintenanceStatusDialogType(type);
    setCurrentMaintenanceStatus(item);
    setMaintenanceStatusDialogOpen(true);
  };

  const handleSaveMaintenanceStatus = async () => {
    if (!currentMaintenanceStatus) return;

    // التحقق من صحة البيانات
    if (!currentMaintenanceStatus.name.trim()) {
      setSnackbar({
        open: true,
        message: 'يرجى إدخال اسم الفئة',
        severity: 'error'
      });
      return;
    }

    if (maintenanceStatusDialogType === 'subcategory' && !(currentMaintenanceStatus as MaintenanceStatusSubCategory).categoryId) {
      setSnackbar({
        open: true,
        message: 'يرجى اختيار الفئة الرئيسية',
        severity: 'error'
      });
      return;
    }

    try {
      if (maintenanceStatusDialogType === 'category') {
        const category = currentMaintenanceStatus as MaintenanceStatusCategory;
        if (category.id) {
          // تحديث فئة موجودة
          await MaintenanceStatusCategoryService.updateCategory(category.id, {
            name: category.name,
            description: category.description,
            icon: category.icon,
            color: category.color,
            order: category.order,
            isActive: category.isActive
          });
        } else {
          // إضافة فئة جديدة
          await MaintenanceStatusCategoryService.addCategory({
            name: category.name,
            description: category.description,
            icon: category.icon,
            color: category.color,
            order: category.order
          }, currentUser?.id || '');
        }
      } else {
        const subCategory = currentMaintenanceStatus as MaintenanceStatusSubCategory;
        if (subCategory.id) {
          // تحديث فئة فرعية موجودة
          await MaintenanceStatusSubCategoryService.updateSubCategory(subCategory.id, {
            name: subCategory.name,
            description: subCategory.description,
            icon: subCategory.icon,
            color: subCategory.color,
            order: subCategory.order,
            isActive: subCategory.isActive
          });
        } else {
          // إضافة فئة فرعية جديدة
          await MaintenanceStatusSubCategoryService.addSubCategory({
            categoryId: subCategory.categoryId,
            name: subCategory.name,
            description: subCategory.description,
            icon: subCategory.icon,
            color: subCategory.color,
            order: subCategory.order
          }, currentUser?.id || '');
        }
      }

      setSnackbar({
        open: true,
        message: 'تم حفظ حالة الصيانة بنجاح',
        severity: 'success'
      });

      setMaintenanceStatusDialogOpen(false);
      setCurrentMaintenanceStatus(null);
      loadMaintenanceStatusData();
    } catch (error: any) {
      console.error('Error saving maintenance status:', error);
      setSnackbar({
        open: true,
        message: `حدث خطأ أثناء حفظ حالة الصيانة: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handleDeleteMaintenanceStatus = async (id: string, type: 'category' | 'subcategory') => {
    if (!window.confirm('هل أنت متأكد من حذف هذا العنصر؟')) return;

    try {
      if (type === 'category') {
        await MaintenanceStatusCategoryService.deleteCategory(id);
      } else {
        await MaintenanceStatusSubCategoryService.deleteSubCategory(id);
      }

      setSnackbar({
        open: true,
        message: 'تم حذف العنصر بنجاح',
        severity: 'success'
      });

      loadMaintenanceStatusData();
    } catch (error: any) {
      console.error('Error deleting maintenance status:', error);
      setSnackbar({
        open: true,
        message: `حدث خطأ أثناء حذف العنصر: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handleAddDefaultMaintenanceStatusData = async () => {
    if (!window.confirm('هل تريد إضافة البيانات الافتراضية لحالات الصيانة؟')) return;

    try {
      await addDefaultMaintenanceStatusData(currentUser?.id || '');
      
      setSnackbar({
        open: true,
        message: 'تم إضافة البيانات الافتراضية لحالات الصيانة بنجاح',
        severity: 'success'
      });

      loadMaintenanceStatusData();
    } catch (error: any) {
      console.error('Error adding default maintenance status data:', error);
      setSnackbar({
        open: true,
        message: `حدث خطأ أثناء إضافة البيانات الافتراضية: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // تغيير التبويب النشط
  const handleTabChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // فتح نموذج إضافة عنصر جديد
  const handleAddItem = (itemType: string) => {
    setItemForm({
      id: '',
      name: '',
      isActive: true,
      itemType
    });
  };

  // فتح نموذج تعديل عنصر
  const handleEditItem = (id: string, name: string, isActive: boolean, itemType: string) => {
    setItemForm({
      id,
      name,
      isActive,
      itemType
    });
  };

  // حفظ العنصر (إضافة/تعديل)
  const handleSaveItem = async () => {
    if (!itemForm.name.trim()) {
      setSnackbar({
        open: true,
        message: 'يرجى إدخال اسم العنصر',
        severity: 'error'
      });
      return;
    }

    try {
      switch (itemForm.itemType) {
        case 'deviceType':
          if (itemForm.id) {
            await updateDeviceType(itemForm.id, itemForm.name, itemForm.isActive);
            setSnackbar({
              open: true,
              message: 'تم تحديث نوع الجهاز بنجاح',
              severity: 'success'
            });
          } else {
            await addDeviceType(itemForm.name);
            setSnackbar({
              open: true,
              message: 'تمت إضافة نوع الجهاز بنجاح',
              severity: 'success'
            });
          }
          loadDeviceTypes();
          break;

        case 'deviceSize':
          if (itemForm.id) {
            await updateDeviceSize(itemForm.id, itemForm.name, itemForm.isActive);
            setSnackbar({
              open: true,
              message: 'تم تحديث حجم الجهاز بنجاح',
              severity: 'success'
            });
          } else {
            await addDeviceSize(itemForm.name);
            setSnackbar({
              open: true,
              message: 'تمت إضافة حجم الجهاز بنجاح',
              severity: 'success'
            });
          }
          loadDeviceSizes();
          break;

        case 'deviceAccessory':
          if (itemForm.id) {
            await updateDeviceAccessory(itemForm.id, itemForm.name, itemForm.isActive);
            setSnackbar({
              open: true,
              message: 'تم تحديث ملحق الجهاز بنجاح',
              severity: 'success'
            });
          } else {
            await addDeviceAccessory(itemForm.name);
            setSnackbar({
              open: true,
              message: 'تمت إضافة ملحق الجهاز بنجاح',
              severity: 'success'
            });
          }
          loadDeviceAccessories();
          break;

        case 'deviceBrand':
          if (itemForm.id) {
            await updateDeviceBrand(itemForm.id, itemForm.name, itemForm.isActive);
            setSnackbar({
              open: true,
              message: 'تم تحديث ماركة الجهاز بنجاح',
              severity: 'success'
            });
          } else {
            await addDeviceBrand(itemForm.name);
            setSnackbar({
              open: true,
              message: 'تمت إضافة ماركة الجهاز بنجاح',
              severity: 'success'
            });
          }
          loadDeviceBrands();
          break;
      }

      // إعادة تعيين نموذج الإضافة/التعديل
      setItemForm({
        id: '',
        name: '',
        isActive: true,
        itemType: ''
      });
    } catch (error) {
      console.error('Error saving item:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حفظ العنصر',
        severity: 'error'
      });
    }
  };

  // إلغاء نموذج الإضافة/التعديل
  const handleCancelForm = () => {
    setItemForm({
      id: '',
      name: '',
      isActive: true,
      itemType: ''
    });
  };

  // فتح مربع حوار الحذف
  const handleConfirmDelete = (id: string, name: string, itemType: string) => {
    setDialog({
      open: true,
      title: 'تأكيد الحذف',
      message: `هل أنت متأكد من حذف "${name}"؟`,
      itemId: id,
      itemType,
      confirmAction: () => handleDeleteItem(id, itemType)
    });
  };

  // تنفيذ حذف العنصر
  const handleDeleteItem = async (id: string, itemType: string) => {
    try {
      switch (itemType) {
        case 'deviceType':
          await deleteDeviceType(id);
          setSnackbar({
            open: true,
            message: 'تم حذف نوع الجهاز بنجاح',
            severity: 'success'
          });
          loadDeviceTypes();
          break;

        case 'deviceSize':
          await deleteDeviceSize(id);
          setSnackbar({
            open: true,
            message: 'تم حذف حجم الجهاز بنجاح',
            severity: 'success'
          });
          loadDeviceSizes();
          break;

        case 'deviceAccessory':
          await deleteDeviceAccessory(id);
          setSnackbar({
            open: true,
            message: 'تم حذف ملحق الجهاز بنجاح',
            severity: 'success'
          });
          loadDeviceAccessories();
          break;

        case 'deviceBrand':
          await deleteDeviceBrand(id);
          setSnackbar({
            open: true,
            message: 'تم حذف ماركة الجهاز بنجاح',
            severity: 'success'
          });
          loadDeviceBrands();
          break;
      }

      // إغلاق مربع حوار الحذف
      setDialog({ ...dialog, open: false });
    } catch (error) {
      console.error('Error deleting item:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حذف العنصر',
        severity: 'error'
      });
    }
  };

  // إغلاق مربع حوار الحذف
  const handleCloseDialog = () => {
    setDialog({ ...dialog, open: false });
  };

  // تعيين نوع جهاز كافتراضي
  const handleSetDefaultDeviceType = async (id: string, name: string) => {
    try {
      await setDefaultDeviceType(id);
      loadDeviceTypes();
      setSnackbar({
        open: true,
        message: `تم تعيين "${name}" كنوع افتراضي`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error setting default device type:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تعيين النوع الافتراضي',
        severity: 'error'
      });
    }
  };

  // renderization of item form
  const renderItemForm = (itemType: string) => {
    if (itemForm.itemType !== itemType) return null;

    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          {itemForm.id ? 'تعديل العنصر' : 'إضافة عنصر جديد'}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="الاسم"
              value={itemForm.name}
              onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
              required
            />
          </Grid>
          {itemForm.id && (
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={itemForm.isActive}
                    onChange={(e) => setItemForm({ ...itemForm, isActive: e.target.checked })}
                  />
                }
                label="نشط"
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={handleCancelForm}
              >
                إلغاء
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveItem}
                disabled={!itemForm.name.trim()}
              >
                حفظ
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  // renderization of item list
  const renderItemList = (items: any[], itemType: string, emptyMessage: string) => {
    if (loading.types && itemType === 'deviceType') {
      return (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (loading.sizes && itemType === 'deviceSize') {
      return (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (loading.accessories && itemType === 'deviceAccessory') {
      return (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (loading.brands && itemType === 'deviceBrand') {
      return (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (items.length === 0) {
      return (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Paper>
      );
    }

    return (
      <List component={Paper}>
        {items.map((item) => (
          <React.Fragment key={item.id}>
            <ListItem
              secondaryAction={
                <Box>
                  {/* زر تعيين كافتراضي لأنواع الأجهزة فقط */}
                  {itemType === 'deviceType' && !item.isDefault && (
                    <IconButton
                      edge="end"
                      aria-label="set-default"
                      onClick={() => handleSetDefaultDeviceType(item.id, item.name)}
                      title="تعيين كافتراضي"
                    >
                      <StarBorderIcon />
                    </IconButton>
                  )}
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    onClick={() => handleEditItem(item.id, item.name, item.isActive, itemType)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleConfirmDelete(item.id, item.name, itemType)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              }
            >
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center">
                    {item.name}
                    {itemType === 'deviceType' && item.isDefault && (
                      <Chip
                        label="افتراضي"
                        size="small"
                        color="primary"
                        sx={{ ml: 1 }}
                        icon={<StarIcon />}
                      />
                    )}
                  </Box>
                }
                secondary={item.isActive ? 'نشط' : 'غير نشط'}
                primaryTypographyProps={{
                  style: {
                    color: item.isActive ? 'inherit' : '#888',
                  },
                }}
              />
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        الإعدادات
      </Typography>

      {/* تبويبات الإعدادات */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 1 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ flex: 1 }}
          >
            <Tab value="general" label="عام" icon={<BusinessIcon />} iconPosition="start" />
            <Tab value="users" label="المستخدمين" icon={<PersonIcon />} iconPosition="start" />
            <Tab value="security" label="الأمان" icon={<VpnKeyIcon />} iconPosition="start" />
            <Tab value="notifications" label="الإشعارات" icon={<NotificationsIcon />} iconPosition="start" />
            <Tab value="deviceTypes" label="أنواع الأجهزة" icon={<BusinessIcon />} iconPosition="start" />
            <Tab value="deviceSizes" label="أحجام الأجهزة" icon={<BusinessIcon />} iconPosition="start" />
            <Tab value="deviceAccessories" label="ملحقات الأجهزة" icon={<BusinessIcon />} iconPosition="start" />
            <Tab value="deviceBrands" label="ماركات الأجهزة" icon={<BusinessIcon />} iconPosition="start" />
            <Tab value="maintenanceStatus" label="حالات الصيانة" icon={<BuildIcon />} iconPosition="start" />
            <Tab value="print" label="الطباعة" icon={<PrintIcon />} iconPosition="start" />
          </Tabs>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleGoToAdvancedSettings}
            startIcon={<SettingsIcon />}
            sx={{ ml: 2 }}
          >
            إعدادات متقدمة
          </Button>
        </Box>
      </Paper>

      {/* محتوى التبويب: الإعدادات العامة */}
      {currentTab === 'general' && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            إعدادات عامة
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="اسم الشركة"
                name="companyName"
                value={settings.companyName}
                onChange={handleSettingsChange}
                fullWidth
                margin="normal"
              />
              <TextField
                label="عنوان الشركة"
                name="companyAddress"
                value={settings.companyAddress}
                onChange={handleSettingsChange}
                fullWidth
                margin="normal"
              />
              <TextField
                label="رقم الهاتف"
                name="companyPhone"
                value={settings.companyPhone}
                onChange={handleSettingsChange}
                fullWidth
                margin="normal"
              />
              <TextField
                label="الرقم الضريبي"
                name="vatNumber"
                value={settings.vatNumber}
                onChange={handleSettingsChange}
                fullWidth
                margin="normal"
              />
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  شعار الشركة
                </Typography>
                {settings.companyLogo ? (
                  <Box mb={2} display="flex" flexDirection="column" alignItems="center">
                    <Box component="img" src={settings.companyLogo} alt="شعار الشركة" sx={{ width: 200, mb: 1 }} />
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => setSettings({ ...settings, companyLogo: undefined })}
                    >
                      حذف الشعار
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AddIcon />}
                    fullWidth
                  >
                    تحميل شعار
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleLogoUpload}
                    />
                  </Button>
                )}
              </Box>

              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  أيقونة الموقع (Favicon)
                </Typography>
                {settings.companyFavicon ? (
                  <Box mb={2} display="flex" flexDirection="column" alignItems="center">
                    <Box component="img" src={settings.companyFavicon} alt="أيقونة الموقع" sx={{ width: 64, height: 64, mb: 1 }} />
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        setSettings({ ...settings, companyFavicon: undefined });
                        restoreDefaultFavicon();
                      }}
                    >
                      حذف الأيقونة
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AddIcon />}
                    fullWidth
                  >
                    تحميل أيقونة الموقع
                    <input
                      type="file"
                      accept="image/x-icon,image/png,image/jpeg,image/svg+xml"
                      hidden
                      onChange={handleFaviconUpload}
                    />
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  يفضل استخدام صورة مربعة بأبعاد 16×16 أو 32×32 أو 64×64 بكسل بتنسيق ICO أو PNG
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>العملة</InputLabel>
                <Select
                  name="currency"
                  value={settings.currency}
                  label="العملة"
                  onChange={handleSelectChange}
                >
                  {currencies.map((currency) => (
                    <MenuItem key={currency.value} value={currency.value}>
                      {currency.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel>اللغة</InputLabel>
                <Select
                  name="language"
                  value={settings.language}
                  label="اللغة"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="ar">العربية</MenuItem>
                  <MenuItem value="en">الإنجليزية</MenuItem>
                </Select>
              </FormControl>
              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.showVat}
                      onChange={handleSettingsChange}
                      name="showVat"
                    />
                  }
                  label="عرض ضريبة القيمة المضافة"
                />
              </Box>
              {settings.showVat && (
                <TextField
                  label="نسبة الضريبة %"
                  name="vatPercentage"
                  type="number"
                  value={settings.vatPercentage}
                  onChange={handleSettingsChange}
                  fullWidth
                  margin="normal"
                  InputProps={{ inputProps: { min: 0, max: 100 } }}
                />
              )}

              {/* إعدادات رقم الهاتف */}
              <Box mt={3}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PhoneIcon color="primary" />
                  إعدادات رقم الهاتف
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.phoneValidationEnabled}
                      onChange={handleSettingsChange}
                      name="phoneValidationEnabled"
                    />
                  }
                  label="تفعيل التحقق من رقم الهاتف"
                />

                {settings.phoneValidationEnabled && (
                  <TextField
                    label="عدد أرقام الهاتف المطلوبة"
                    name="phoneDigitsCount"
                    type="number"
                    value={settings.phoneDigitsCount}
                    onChange={handleSettingsChange}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      inputProps: { min: 7, max: 15 }
                    }}
                    helperText="مثال: 11 رقم لمصر، 10 أرقام للسعودية"
                  />
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  عند تفعيل هذا الخيار، سيتم التحقق من أن رقم الهاتف يحتوي على العدد المحدد من الأرقام عند إضافة أو تعديل العملاء
                </Typography>
              </Box>

              {/* إعدادات رسائل الواتساب */}
              <Box mt={3}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PhoneIcon color="primary" />
                  إعدادات رسائل الواتساب
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.whatsappEnabled}
                      onChange={handleSettingsChange}
                      name="whatsappEnabled"
                    />
                  }
                  label="تفعيل إرسال رسائل الواتساب للعملاء"
                />

                {settings.whatsappEnabled && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.whatsappAutoSendOnCreate || false}
                          onChange={handleSettingsChange}
                          name="whatsappAutoSendOnCreate"
                        />
                      }
                      label="إرسال رسالة تلقائياً للعميل عند حفظ طلب الصيانة"
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      عند تفعيل هذا الخيار، سيتم إرسال رسالة استلام الجهاز تلقائياً للعميل فور حفظ طلب الصيانة
                    </Typography>
                  </Box>
                )}

                {settings.whatsappEnabled && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      label="رسالة استلام الجهاز"
                      name="whatsappMessages.deviceReceived"
                      multiline
                      rows={3}
                      value={settings.whatsappMessages?.deviceReceived || ''}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          whatsappMessages: {
                            ...prev.whatsappMessages,
                            deviceReceived: e.target.value
                          }
                        }));
                      }}
                      fullWidth
                      margin="normal"
                      helperText="المتغيرات المتاحة: {customerName} (اسم العميل), {deviceBrand} (ماركة الجهاز), {deviceSize} (حجم الجهاز), {cost} (التكلفة), {orderId} (رقم الطلب)"
                    />

                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={async () => {
                          try {
                            const { whatsappTemplatesService } = await import('../services/whatsappTemplatesService');
                            await whatsappTemplatesService.updateTemplatesForDeviceBrand();
                            alert('تم تحديث قوالب الرسائل بنجاح لاستخدام ماركة الجهاز');
                          } catch (error) {
                            console.error('Error updating templates:', error);
                            alert('حدث خطأ أثناء تحديث القوالب');
                          }
                        }}
                      >
                        تحديث القوالب لاستخدام ماركة الجهاز
                      </Button>
                    </Box>

                    <TextField
                      label="رسالة التكلفة أثناء العمل"
                      name="whatsappMessages.deviceInProgress"
                      multiline
                      rows={3}
                      value={settings.whatsappMessages?.deviceInProgress || ''}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          whatsappMessages: {
                            ...prev.whatsappMessages,
                            deviceInProgress: e.target.value
                          }
                        }));
                      }}
                      fullWidth
                      margin="normal"
                      helperText="المتغيرات المتاحة: {customerName} (اسم العميل), {deviceBrand} (ماركة الجهاز), {deviceSize} (حجم الجهاز), {cost} (التكلفة), {orderId} (رقم الطلب) - للعملاء الذين يطلبون الاتصال قبل الإصلاح"
                    />

                    <TextField
                      label="رسالة اكتمال الإصلاح"
                      name="whatsappMessages.deviceCompleted"
                      multiline
                      rows={3}
                      value={settings.whatsappMessages?.deviceCompleted || ''}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          whatsappMessages: {
                            ...prev.whatsappMessages,
                            deviceCompleted: e.target.value
                          }
                        }));
                      }}
                      fullWidth
                      margin="normal"
                      helperText="المتغيرات المتاحة: {customerName} (اسم العميل), {deviceBrand} (ماركة الجهاز), {deviceSize} (حجم الجهاز), {orderId} (رقم الطلب)"
                    />

                    <TextField
                      label="رسالة الاستعجال"
                      name="whatsappMessages.deviceUrgent"
                      multiline
                      rows={3}
                      value={settings.whatsappMessages?.deviceUrgent || ''}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          whatsappMessages: {
                            ...prev.whatsappMessages,
                            deviceUrgent: e.target.value
                          }
                        }));
                      }}
                      fullWidth
                      margin="normal"
                      helperText="المتغيرات المتاحة: {customerName} (اسم العميل), {deviceBrand} (ماركة الجهاز), {deviceSize} (حجم الجهاز), {orderId} (رقم الطلب) - ترسل تلقائياً للأجهزة المكتملة غير المستلمة كل 48 ساعة"
                    />
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  عند تفعيل هذا الخيار، سيتم إرسال رسائل واتساب تلقائية للعملاء عند استلام الجهاز وعند اكتمال الإصلاح ورسائل استعجال للأجهزة غير المستلمة
                </Typography>
              </Box>
            </Grid>

            {/* مكون إدارة رسائل الاستعجال */}
            <Grid item xs={12}>
              <UrgentMessageManager />
            </Grid>
          </Grid>
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
            >
              حفظ الإعدادات
            </Button>
          </Box>
        </Paper>
      )}

      {/* محتوى التبويب: المستخدمين */}
      {currentTab === 'users' && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              إدارة المستخدمين
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddUser}
            >
              إضافة مستخدم
            </Button>
          </Box>

          {loadingUsers ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : users.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                لا يوجد مستخدمين مسجلين بعد.
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>الاسم</TableCell>
                    <TableCell>البريد الإلكتروني</TableCell>
                    <TableCell>الدور</TableCell>
                    <TableCell>الفرع</TableCell>
                    <TableCell>الحالة</TableCell>
                    <TableCell>الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ bgcolor: user.role === UserRole.ADMIN ? 'primary.main' : 'secondary.main', mr: 1 }}>
                            {user.name ? user.name.charAt(0) : 'U'}
                          </Avatar>
                          {user.name}
                        </Box>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{translateRole(user.role)}</TableCell>
                      <TableCell>{user.branchName || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.isActive ? 'نشط' : 'غير نشط'}
                          color={user.isActive ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleEditUser(user)} title="تعديل">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleManagePermissions(user)}
                          title="إدارة الصلاحيات"
                        >
                          <VpnKeyIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteConfirmation(user.id)}
                          title="حذف"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color={user.isActive ? 'error' : 'success'}
                          onClick={() => handleToggleUserActive(user.id, !user.isActive)}
                          title={user.isActive ? 'تعطيل' : 'تفعيل'}
                        >
                          {user.isActive ? <CloseIcon fontSize="small" /> : <CheckIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* محتوى التبويب: الأمان */}
      {currentTab === 'security' && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            إعدادات الأمان
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <VpnKeyIcon />
              </ListItemIcon>
              <ListItemText
                primary="تغيير كلمة المرور"
                secondary="قم بتغيير كلمة المرور الخاصة بك"
              />
              <Button
                variant="outlined"
                onClick={handleChangePasswordClick}
              >
                تغيير
              </Button>
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText
                primary="جلسات تسجيل الدخول النشطة"
                secondary="إدارة جلسات تسجيل الدخول النشطة على جميع الأجهزة"
              />
              <Button
                variant="outlined"
                color="error"
              >
                تسجيل الخروج من كل الأجهزة
              </Button>
            </ListItem>
          </List>
        </Paper>
      )}

      {/* محتوى التبويب: الإشعارات */}
      {currentTab === 'notifications' && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            إعدادات الإشعارات
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <NotificationsIcon />
              </ListItemIcon>
              <ListItemText
                primary="تفعيل الإشعارات"
                secondary="استلام إشعارات تحديثات النظام والطلبات الجديدة"
              />
              <ListItemSecondaryAction>
                <Switch
                  edge="end"
                  checked={settings.enableNotifications}
                  onChange={handleSettingsChange}
                  name="enableNotifications"
                />
              </ListItemSecondaryAction>
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <LanguageIcon />
              </ListItemIcon>
              <ListItemText
                primary="إشعارات البريد الإلكتروني"
                secondary="استلام إشعارات عبر البريد الإلكتروني"
              />
              <ListItemSecondaryAction>
                <Switch
                  edge="end"
                  checked={settings.emailNotifications}
                  onChange={handleSettingsChange}
                  name="emailNotifications"
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
            >
              حفظ الإعدادات
            </Button>
          </Box>
        </Paper>
      )}

      {/* تم نقل قسم النسخ الاحتياطي إلى صفحة منفصلة تحت قائمة الإعدادات */}

      {/* محتوى التبويب: أنواع الأجهزة */}
      {currentTab === 'deviceTypes' && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">أنواع الأجهزة</Typography>
            {itemForm.itemType !== 'deviceType' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleAddItem('deviceType')}
              >
                إضافة نوع جديد
              </Button>
            )}
          </Box>

          {renderItemForm('deviceType')}
          {renderItemList(deviceTypes, 'deviceType', 'لا توجد أنواع أجهزة مسجلة بعد')}
        </Paper>
      )}

      {/* محتوى التبويب: أحجام الأجهزة */}
      {currentTab === 'deviceSizes' && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">أحجام الأجهزة</Typography>
            {itemForm.itemType !== 'deviceSize' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleAddItem('deviceSize')}
              >
                إضافة حجم جديد
              </Button>
            )}
          </Box>

          {renderItemForm('deviceSize')}
          {renderItemList(deviceSizes, 'deviceSize', 'لا توجد أحجام أجهزة مسجلة بعد')}
        </Paper>
      )}

      {/* محتوى التبويب: ملحقات الأجهزة */}
      {currentTab === 'deviceAccessories' && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">ملحقات الأجهزة</Typography>
            {itemForm.itemType !== 'deviceAccessory' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleAddItem('deviceAccessory')}
              >
                إضافة ملحق جديد
              </Button>
            )}
          </Box>

          {renderItemForm('deviceAccessory')}
          {renderItemList(deviceAccessories, 'deviceAccessory', 'لا توجد ملحقات أجهزة مسجلة بعد')}
        </Paper>
      )}

      {/* محتوى التبويب: ماركات الأجهزة */}
      {currentTab === 'deviceBrands' && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">ماركات الأجهزة</Typography>
            {itemForm.itemType !== 'deviceBrand' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleAddItem('deviceBrand')}
              >
                إضافة ماركة جديدة
              </Button>
            )}
          </Box>

          {renderItemForm('deviceBrand')}
          {renderItemList(deviceBrands, 'deviceBrand', 'لا توجد ماركات أجهزة مسجلة بعد')}
        </Paper>
      )}

      {/* محتوى التبويب: حالات الصيانة */}
      {currentTab === 'maintenanceStatus' && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">
              إدارة حالات الصيانة
            </Typography>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleAddDefaultMaintenanceStatusData}
              disabled={loadingMaintenanceStatus}
            >
              إضافة بيانات افتراضية
            </Button>
          </Box>
          
          {loadingMaintenanceStatus ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* الفئات الرئيسية */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" color="primary">
                      الفئات الرئيسية
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddMaintenanceStatus('category')}
                    >
                      إضافة فئة
                    </Button>
                  </Box>
                  
                  {maintenanceStatusCategories.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" py={3}>
                      لا توجد فئات رئيسية
                    </Typography>
                  ) : (
                    <List>
                      {maintenanceStatusCategories.map((category) => (
                        <ListItem
                          key={category.id}
                          sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                            backgroundColor: category.isActive ? 'background.paper' : 'action.hover'
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle1" fontWeight="medium">
                                  {category.name}
                                </Typography>
                                {!category.isActive && (
                                  <Chip label="غير نشط" size="small" color="warning" />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                {category.description && (
                                  <Typography variant="body2" color="text.secondary">
                                    {category.description}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  الترتيب: {category.order}
                                </Typography>
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() => handleEditMaintenanceStatus(category, 'category')}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              edge="end"
                              onClick={() => handleDeleteMaintenanceStatus(category.id, 'category')}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>

              {/* الفئات الفرعية */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" color="primary">
                      الفئات الفرعية
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddMaintenanceStatus('subcategory')}
                    >
                      إضافة فئة فرعية
                    </Button>
                  </Box>
                  
                  {maintenanceStatusSubCategories.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" py={3}>
                      لا توجد فئات فرعية
                    </Typography>
                  ) : (
                    <List>
                      {maintenanceStatusSubCategories.map((subCategory) => {
                        const parentCategory = maintenanceStatusCategories.find(cat => cat.id === subCategory.categoryId);
                        return (
                          <ListItem
                            key={subCategory.id}
                            sx={{
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 1,
                              mb: 1,
                              backgroundColor: subCategory.isActive ? 'background.paper' : 'action.hover'
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="subtitle1" fontWeight="medium">
                                    {subCategory.name}
                                  </Typography>
                                  {!subCategory.isActive && (
                                    <Chip label="غير نشط" size="small" color="warning" />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  {parentCategory && (
                                    <Typography variant="body2" color="primary">
                                      الفئة الرئيسية: {parentCategory.name}
                                    </Typography>
                                  )}
                                  {subCategory.description && (
                                    <Typography variant="body2" color="text.secondary">
                                      {subCategory.description}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary">
                                    الترتيب: {subCategory.order}
                                  </Typography>
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => handleEditMaintenanceStatus(subCategory, 'subcategory')}
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                edge="end"
                                onClick={() => handleDeleteMaintenanceStatus(subCategory.id, 'subcategory')}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </Paper>
      )}

      {/* محتوى التبويب: إعدادات الطباعة */}
      {currentTab === 'print' && (
        <Paper sx={{ p: 3 }}>
          <PrintSettings
            onSave={(settings) => {
              setSnackbar({
                open: true,
                message: 'تم حفظ إعدادات الطباعة بنجاح',
                severity: 'success'
              });
            }}
            onError={(error) => {
              setSnackbar({
                open: true,
                message: error,
                severity: 'error'
              });
            }}
            onSuccess={(message) => {
              setSnackbar({
                open: true,
                message: message,
                severity: 'success'
              });
            }}
          />
        </Paper>
      )}

      {/* نافذة حوار إضافة/تعديل حالات الصيانة */}
      <Dialog 
        open={maintenanceStatusDialogOpen} 
        onClose={() => setMaintenanceStatusDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {currentMaintenanceStatus ? 'تعديل حالة الصيانة' : 'إضافة حالة صيانة جديدة'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="الاسم"
                fullWidth
                value={currentMaintenanceStatus?.name || ''}
                onChange={(e) => setCurrentMaintenanceStatus(prev => 
                  prev ? { ...prev, name: e.target.value } : null
                )}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="الترتيب"
                type="number"
                fullWidth
                value={currentMaintenanceStatus?.order || 0}
                onChange={(e) => setCurrentMaintenanceStatus(prev => 
                  prev ? { ...prev, order: parseInt(e.target.value) || 0 } : null
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="الوصف"
                fullWidth
                multiline
                rows={3}
                value={currentMaintenanceStatus?.description || ''}
                onChange={(e) => setCurrentMaintenanceStatus(prev => 
                  prev ? { ...prev, description: e.target.value } : null
                )}
              />
            </Grid>
            {maintenanceStatusDialogType === 'subcategory' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>الفئة الرئيسية</InputLabel>
                  <Select
                    value={(currentMaintenanceStatus as MaintenanceStatusSubCategory)?.categoryId || ''}
                    onChange={(e) => setCurrentMaintenanceStatus(prev => 
                      prev ? { ...prev, categoryId: e.target.value } : null
                    )}
                    label="الفئة الرئيسية"
                  >
                    {maintenanceStatusCategories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                label="الأيقونة (اختياري)"
                fullWidth
                value={currentMaintenanceStatus?.icon || ''}
                onChange={(e) => setCurrentMaintenanceStatus(prev => 
                  prev ? { ...prev, icon: e.target.value } : null
                )}
                placeholder="مثال: build, settings, warning"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="اللون (اختياري)"
                fullWidth
                value={currentMaintenanceStatus?.color || ''}
                onChange={(e) => setCurrentMaintenanceStatus(prev => 
                  prev ? { ...prev, color: e.target.value } : null
                )}
                placeholder="مثال: primary, success, warning, error"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentMaintenanceStatus?.isActive ?? true}
                    onChange={(e) => setCurrentMaintenanceStatus(prev => 
                      prev ? { ...prev, isActive: e.target.checked } : null
                    )}
                  />
                }
                label="نشط"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMaintenanceStatusDialogOpen(false)}>
            إلغاء
          </Button>
          <Button onClick={handleSaveMaintenanceStatus} variant="contained" color="primary">
            {currentMaintenanceStatus ? 'تحديث' : 'إضافة'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* نافذة حوار تغيير كلمة المرور */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          تغيير كلمة المرور
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="كلمة المرور الحالية"
                type="password"
                fullWidth
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="كلمة المرور الجديدة"
                type="password"
                fullWidth
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="تأكيد كلمة المرور الجديدة"
                type="password"
                fullWidth
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>إلغاء</Button>
          <Button onClick={handleSavePassword} variant="contained" color="primary">
            تغيير كلمة المرور
          </Button>
        </DialogActions>
      </Dialog>

      {/* مربع حوار تأكيد الحذف */}
      <Dialog
        open={dialog.open}
        onClose={handleCloseDialog}
      >
        <DialogTitle>{dialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            إلغاء
          </Button>
          <Button onClick={() => dialog.confirmAction()} color="error" autoFocus>
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار إضافة/تعديل المستخدم */}
      <Dialog
        open={userDialogOpen}
        onClose={() => setUserDialogOpen(false)}
        aria-labelledby="user-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="user-dialog-title">
          {currentUser_ ? 'تعديل بيانات المستخدم' : 'إضافة مستخدم جديد'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="الاسم"
                name="name"
                value={newUser.name || ''}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="البريد الإلكتروني"
                name="email"
                type="email"
                value={newUser.email || ''}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                fullWidth
                required
                disabled={!!currentUser_} // لا يمكن تغيير البريد الإلكتروني للمستخدم الموجود
              />
            </Grid>
            {!currentUser_ && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="كلمة المرور"
                    name="password"
                    type="password"
                    value={newUser.password || ''}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="تأكيد كلمة المرور"
                    name="confirmPassword"
                    type="password"
                    value={newUser.confirmPassword || ''}
                    onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                    fullWidth
                    required
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel id="role-label">الدور</InputLabel>
                <Select
                  labelId="role-label"
                  name="role"
                  value={newUser.role || ''}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value as UserRole })}
                  label="الدور"
                >
                  <MenuItem value={UserRole.ADMIN}>مدير النظام</MenuItem>
                  <MenuItem value={UserRole.BRANCH_MANAGER}>مدير فرع</MenuItem>
                  <MenuItem value={UserRole.WAREHOUSE_MANAGER}>مدير مخزن</MenuItem>
                  <MenuItem value={UserRole.TECHNICIAN}>فني صيانة</MenuItem>
                  <MenuItem value={UserRole.CASHIER}>أمين صندوق</MenuItem>
                  <MenuItem value={UserRole.INVENTORY_CLERK}>موظف مخزن</MenuItem>
                  <MenuItem value={UserRole.SALES}>مندوب مبيعات</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="branch-label">الفرع</InputLabel>
                <Select
                  labelId="branch-label"
                  name="selectedBranch"
                  value={newUser.selectedBranch || ''}
                  onChange={(e) => setNewUser({ ...newUser, selectedBranch: e.target.value as string })}
                  label="الفرع"
                >
                  <MenuItem value="">لا يوجد</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newUser.isActive || false}
                    onChange={(e) => setNewUser({ ...newUser, isActive: e.target.checked })}
                    name="isActive"
                    color="primary"
                  />
                }
                label="مستخدم نشط"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)} color="inherit">
            إلغاء
          </Button>
          <Button onClick={handleSaveUser} color="primary" variant="contained">
            {currentUser_ ? 'تحديث' : 'إضافة'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار تأكيد حذف المستخدم */}
      <Dialog
        open={deleteUserDialogOpen}
        onClose={() => setDeleteUserDialogOpen(false)}
        aria-labelledby="delete-user-dialog-title"
        aria-describedby="delete-user-dialog-description"
      >
        <DialogTitle id="delete-user-dialog-title">
          تأكيد حذف المستخدم
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-user-dialog-description">
            هل أنت متأكد من حذف هذا المستخدم؟ لا يمكن التراجع عن هذا الإجراء.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUserDialogOpen(false)} color="inherit">
            إلغاء
          </Button>
          <Button
            onClick={handleDeleteUser}
            color="error"
            variant="contained"
            disabled={deleteUserLoading}
            startIcon={deleteUserLoading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {deleteUserLoading ? 'جاري الحذف...' : 'حذف'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار إدارة صلاحيات المستخدم */}
      <Dialog
        open={permissionsDialogOpen}
        onClose={() => setPermissionsDialogOpen(false)}
        aria-labelledby="permissions-dialog-title"
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle id="permissions-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VpnKeyIcon color="primary" />
          إدارة صلاحيات المستخدم: {userForPermissions?.name}
        </DialogTitle>
        <DialogContent>
          {userPermissions && (
            <>
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl variant="outlined" sx={{ minWidth: 250 }}>
                  <InputLabel id="permission-template-label">قالب الصلاحيات</InputLabel>
                  <Select
                    labelId="permission-template-label"
                    label="قالب الصلاحيات"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        setUserPermissions(getDefaultPermissions(e.target.value as UserRole));
                      }
                    }}
                  >
                    <MenuItem value="">اختر قالب الصلاحيات</MenuItem>
                    <MenuItem value={UserRole.ADMIN}>مدير النظام</MenuItem>
                    <MenuItem value={UserRole.BRANCH_MANAGER}>مدير فرع</MenuItem>
                    <MenuItem value={UserRole.WAREHOUSE_MANAGER}>مدير مخزن</MenuItem>
                    <MenuItem value={UserRole.TECHNICIAN}>فني صيانة</MenuItem>
                    <MenuItem value={UserRole.CASHIER}>أمين صندوق</MenuItem>
                    <MenuItem value={UserRole.INVENTORY_CLERK}>موظف مخزن</MenuItem>
                    <MenuItem value={UserRole.SALES}>مندوب مبيعات</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    if (userForPermissions) {
                      setUserPermissions(getDefaultPermissions(userForPermissions.role));
                    }
                  }}
                >
                  إعادة تعيين حسب الدور
                </Button>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DashboardIcon color="primary" fontSize="small" />
                    صلاحيات الوصول للصفحات
                  </Typography>
                  <Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setUserPermissions({
                          ...userPermissions,
                          canAccessDashboard: true,
                          canAccessInventory: true,
                          canAccessPOS: true,
                          canAccessMaintenance: true,
                          canAccessCustomers: true,
                          canAccessSettings: true,
                          canAccessReports: true
                        });
                      }}
                      sx={{ mr: 1 }}
                    >
                      تحديد الكل
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setUserPermissions({
                          ...userPermissions,
                          canAccessDashboard: false,
                          canAccessInventory: false,
                          canAccessPOS: false,
                          canAccessMaintenance: false,
                          canAccessCustomers: false,
                          canAccessSettings: false,
                          canAccessReports: false
                        });
                      }}
                    >
                      إلغاء تحديد الكل
                    </Button>
                  </Box>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canAccessDashboard}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canAccessDashboard: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="الوصول للوحة الرئيسية"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canAccessInventory}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canAccessInventory: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="الوصول للمخزون"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canAccessPOS}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canAccessPOS: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="الوصول لنقاط البيع"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canAccessMaintenance}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canAccessMaintenance: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="الوصول للصيانة"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canAccessCustomers}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canAccessCustomers: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="الوصول للعملاء"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canAccessSettings}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canAccessSettings: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="الوصول للإعدادات"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canAccessReports}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canAccessReports: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="الوصول للتقارير"
                    />
                  </Grid>
                </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InventoryIcon color="primary" fontSize="small" />
                    صلاحيات المخزون
                  </Typography>
                  <Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setUserPermissions({
                          ...userPermissions,
                          canViewInventory: true,
                          canAddInventory: true,
                          canEditInventory: true,
                          canDeleteInventory: true,
                          canTransferInventory: true
                        });
                      }}
                      sx={{ mr: 1 }}
                    >
                      تحديد الكل
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setUserPermissions({
                          ...userPermissions,
                          canViewInventory: false,
                          canAddInventory: false,
                          canEditInventory: false,
                          canDeleteInventory: false,
                          canTransferInventory: false
                        });
                      }}
                    >
                      إلغاء تحديد الكل
                    </Button>
                  </Box>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canViewInventory}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canViewInventory: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="عرض المخزون"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canAddInventory}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canAddInventory: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="إضافة مخزون"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canEditInventory}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canEditInventory: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="تعديل مخزون"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canDeleteInventory}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canDeleteInventory: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="حذف مخزون"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canTransferInventory}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canTransferInventory: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="نقل مخزون"
                    />
                  </Grid>
                </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShoppingCartIcon color="primary" fontSize="small" />
                    صلاحيات المبيعات
                  </Typography>
                  <Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setUserPermissions({
                          ...userPermissions,
                          canViewSales: true,
                          canCreateSales: true,
                          canEditSales: true,
                          canDeleteSales: true,
                          canApplyDiscount: true
                        });
                      }}
                      sx={{ mr: 1 }}
                    >
                      تحديد الكل
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setUserPermissions({
                          ...userPermissions,
                          canViewSales: false,
                          canCreateSales: false,
                          canEditSales: false,
                          canDeleteSales: false,
                          canApplyDiscount: false
                        });
                      }}
                    >
                      إلغاء تحديد الكل
                    </Button>
                  </Box>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canViewSales}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canViewSales: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="عرض المبيعات"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canCreateSales}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canCreateSales: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="إنشاء مبيعات"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canEditSales}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canEditSales: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="تعديل مبيعات"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canDeleteSales}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canDeleteSales: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="حذف مبيعات"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canApplyDiscount}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canApplyDiscount: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="تطبيق خصم"
                    />
                  </Grid>
                </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EngineeringIcon color="primary" fontSize="small" />
                    صلاحيات الصيانة
                  </Typography>
                  <Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setUserPermissions({
                          ...userPermissions,
                          canViewMaintenance: true,
                          canCreateMaintenance: true,
                          canEditMaintenance: true,
                          canDeleteMaintenance: true,
                          canAssignTechnician: true,
                          canChangeMaintStatus: true
                        });
                      }}
                      sx={{ mr: 1 }}
                    >
                      تحديد الكل
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setUserPermissions({
                          ...userPermissions,
                          canViewMaintenance: false,
                          canCreateMaintenance: false,
                          canEditMaintenance: false,
                          canDeleteMaintenance: false,
                          canAssignTechnician: false,
                          canChangeMaintStatus: false
                        });
                      }}
                    >
                      إلغاء تحديد الكل
                    </Button>
                  </Box>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canViewMaintenance}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canViewMaintenance: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="عرض الصيانة"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canCreateMaintenance}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canCreateMaintenance: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="إنشاء صيانة"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canEditMaintenance}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canEditMaintenance: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="تعديل صيانة"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canDeleteMaintenance}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canDeleteMaintenance: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="حذف صيانة"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canAssignTechnician}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canAssignTechnician: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="تعيين فني"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canChangeMaintStatus}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canChangeMaintStatus: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="تغيير حالة الصيانة"
                    />
                  </Grid>
                </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon color="primary" fontSize="small" />
                    صلاحيات العملاء
                  </Typography>
                  <Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setUserPermissions({
                          ...userPermissions,
                          canViewCustomers: true,
                          canAddCustomers: true,
                          canEditCustomers: true,
                          canDeleteCustomers: true
                        });
                      }}
                      sx={{ mr: 1 }}
                    >
                      تحديد الكل
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setUserPermissions({
                          ...userPermissions,
                          canViewCustomers: false,
                          canAddCustomers: false,
                          canEditCustomers: false,
                          canDeleteCustomers: false
                        });
                      }}
                    >
                      إلغاء تحديد الكل
                    </Button>
                  </Box>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canViewCustomers}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canViewCustomers: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="عرض العملاء"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canAddCustomers}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canAddCustomers: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="إضافة عملاء"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canEditCustomers}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canEditCustomers: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="تعديل عملاء"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canDeleteCustomers}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canDeleteCustomers: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="حذف عملاء"
                    />
                  </Grid>
                </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MoneyIcon color="primary" fontSize="small" />
                    صلاحيات المصروفات
                  </Typography>
                  <Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setUserPermissions({
                          ...userPermissions,
                          canViewExpenses: true,
                          canViewBasicExpenses: true,
                          canViewDailyExpenses: true,
                          canAddExpenses: true,
                          canEditExpenses: true,
                          canDeleteExpenses: true
                        });
                      }}
                      sx={{ mr: 1 }}
                    >
                      تحديد الكل
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setUserPermissions({
                          ...userPermissions,
                          canViewExpenses: false,
                          canViewBasicExpenses: false,
                          canViewDailyExpenses: false,
                          canAddExpenses: false,
                          canEditExpenses: false,
                          canDeleteExpenses: false
                        });
                      }}
                    >
                      إلغاء تحديد الكل
                    </Button>
                  </Box>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={userPermissions.canViewExpenses}
                            onChange={(e) => setUserPermissions({ ...userPermissions, canViewExpenses: e.target.checked })}
                            color="primary"
                          />
                        }
                        label="عرض المصروفات"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={userPermissions.canViewBasicExpenses}
                            onChange={(e) => setUserPermissions({ ...userPermissions, canViewBasicExpenses: e.target.checked })}
                            color="primary"
                          />
                        }
                        label="عرض المصروفات الأساسية"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={userPermissions.canViewDailyExpenses}
                            onChange={(e) => setUserPermissions({ ...userPermissions, canViewDailyExpenses: e.target.checked })}
                            color="primary"
                          />
                        }
                        label="عرض المصروفات اليومية"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={userPermissions.canAddExpenses}
                            onChange={(e) => setUserPermissions({ ...userPermissions, canAddExpenses: e.target.checked })}
                            color="primary"
                          />
                        }
                        label="إضافة مصروفات"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={userPermissions.canEditExpenses}
                            onChange={(e) => setUserPermissions({ ...userPermissions, canEditExpenses: e.target.checked })}
                            color="primary"
                          />
                        }
                        label="تعديل مصروفات"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={userPermissions.canDeleteExpenses}
                            onChange={(e) => setUserPermissions({ ...userPermissions, canDeleteExpenses: e.target.checked })}
                            color="primary"
                          />
                        }
                        label="حذف مصروفات"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon color="primary" fontSize="small" />
                    صلاحيات الفروع والمخازن
                  </Typography>
                  <Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setUserPermissions({
                          ...userPermissions,
                          canManageBranches: true,
                          canManageWarehouses: true
                        });
                      }}
                      sx={{ mr: 1 }}
                    >
                      تحديد الكل
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setUserPermissions({
                          ...userPermissions,
                          canManageBranches: false,
                          canManageWarehouses: false
                        });
                      }}
                    >
                      إلغاء تحديد الكل
                    </Button>
                  </Box>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canManageBranches}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canManageBranches: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="إدارة الفروع"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canManageWarehouses}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canManageWarehouses: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="إدارة المخازن"
                    />
                  </Grid>
                </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AdminIcon color="primary" fontSize="small" />
                    صلاحيات المستخدمين والإعدادات
                  </Typography>
                  <Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setUserPermissions({
                          ...userPermissions,
                          canManageUsers: true,
                          canViewUsers: true,
                          canManageSettings: true
                        });
                      }}
                      sx={{ mr: 1 }}
                    >
                      تحديد الكل
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setUserPermissions({
                          ...userPermissions,
                          canManageUsers: false,
                          canViewUsers: false,
                          canManageSettings: false
                        });
                      }}
                    >
                      إلغاء تحديد الكل
                    </Button>
                  </Box>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canManageUsers}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canManageUsers: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="إدارة المستخدمين"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canViewUsers}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canViewUsers: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="عرض المستخدمين"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={userPermissions.canManageSettings}
                          onChange={(e) => setUserPermissions({ ...userPermissions, canManageSettings: e.target.checked })}
                          color="primary"
                        />
                      }
                      label="إدارة الإعدادات"
                    />
                  </Grid>
                </Grid>
                </Paper>
              </Grid>
            </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermissionsDialogOpen(false)} color="inherit">
            إلغاء
          </Button>
          <Button onClick={handleSavePermissions} color="primary" variant="contained">
            حفظ الصلاحيات
          </Button>
        </DialogActions>
      </Dialog>

      {/* رسائل التنبيه */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;